<template>
  <div>
    <h3 class="mt-6" style="display:inline-block">教室情報</h3>
    <v-icon class="delete-icon" @click="remove" v-if="index !== 1">
      mdi-delete
    </v-icon>
    <p class="ml-1 mb-0 duplicated__message" v-if="isDuplicatedClassroom">教室名「{{duplicatedClassroomName}}」と全ての入力項目が重複しています。</p>
    <p class="attention">※すべての項目にご記入ください</p>
    <v-row>
      <v-col class="pt-0" cols="12">
        <tbody>
          <tr>
            <th class="table-label">
              1. 教室名 <font color="#ff4a3d" size="2">❇︎</font>
              <br>
              <font size="2">※50文字まで</font>
            </th>
            <td>
              <v-text-field
                class="text-field"
                :value="classroom.name"
                @change="updateClassroom('name', $event)"
                outlined
                dense
                required
                :rules="rules.classroomName"
                color="secondary"
                :disabled="isConfirm"
              ></v-text-field>
            </td>
          </tr>
          <tr>
            <th>
              2. 教室電話番号 <font color="#ff4a3d" size="2">❇︎</font>
              <br>
              <font size="2">※半角数字11文字まで(ハイフンなし)</font>
            </th>
            <td>
              <v-text-field
                class="text-field"
                :value="classroom.tel"
                @change="updateClassroom('tel', $event)"
                outlined
                dense
                required
                :rules="rules.tel"
                color="secondary"
                :disabled="isConfirm"
              ></v-text-field>
            </td>
          </tr>
          <tr>
            <th>
              3. 教室担当者名 <font color="#ff4a3d" size="2">❇︎</font>
              <br>
              <font size="2">※20文字まで</font>
            </th>
            <td>
              <v-text-field
                class="text-field"
                :value="classroom.chargePerson"
                @change="updateClassroom('chargePerson', $event)"
                outlined
                dense
                required
                :rules="rules.classroomChargePerson"
                color="secondary"
                :disabled="isConfirm"
              ></v-text-field>
            </td>
          </tr>
          <tr>
            <th>4. 教室担当者メールアドレス <font color="#ff4a3d" size="2">❇︎</font>
              <v-tooltip top content-class="tooltip__text">
                <template #[`activator`]="{ on, attrs }">
                  <font class="float-right question" v-bind="attrs" v-on="on">？</font>
                </template>
                <span>スマートフォン、タブレットのみのアドレスは不可</span>
              </v-tooltip>
              <br>
              <font size="2">※100文字まで</font>
              <br>
              <font size="2">※許可記号「_」「.」「+」「-」「?」「/」</font>
              <br>
              <font size="2">※「@(アットマーク)」の前は64文字まで</font>
            </th>
            <td>
              <v-text-field
                class="text-field"
                :value="classroom.mail"
                @change="updateClassroom('mail', $event)"
                outlined
                dense
                required
                :rules="rules.mail"
                color="secondary"
                :disabled="isConfirm"
              ></v-text-field>
            </td>
          </tr>

          <!-- 利用開始時期 ※教室追加場合のみ表示する -->
          <tr v-if="isAddClassroom">
            <th>5. 利用開始時期 <font color="#ff4a3d" size="2">❇︎</font></th>
            <td>
              <v-radio-group
                :value="classroom.startTime"
                @change="updateClassroom('startTime', $event)"
                class="radio-group ml-3 mb-3"
                :rules="[v => !!v || '利用開始時期を選択してください']"
                :disabled="isConfirm"
              >
                <table >
                  <tr>
                    <th class="start-time__th">
                      <v-radio
                        label="申込承認後すぐ利用する。"
                        value="start_now"
                        color="secondary"
                      ></v-radio>
                    </th>
                    <td class="start-time__td">
                      弊社登録完了後、すぐに利用を開始する<br>
                      （利用開始のご連絡は、申し込み後5営業日以内にいたします。）
                    </td>
                  </tr>
                  <tr>
                    <th class="start-time__th">
                      <v-radio
                        label="翌月の月初めから利用する。"
                        value="start_next_month"
                        color="secondary"
                      ></v-radio>
                    </th>
                    <td class="start-time__td">
                      翌月初めの営業日より利用を開始する<br>
                      （弊社営業日のため、月初すぐとは限りません）
                    </td>
                  </tr>
                </table>
              </v-radio-group>
            </td>
          </tr>
        </tbody>
      </v-col>
    </v-row>

    <!-- 教室削除時の確認ダイアログ -->
    <v-confirm-dialog
      ref="confirm"
      title="教室削除"
      :message="deleteMessage"
      buttonMessage="削除"
      @confirm="confirmDeletion"
    />
  </div>
</template>

<script>
import rules from '../../rules'
import { omitBy } from 'lodash'
import { mapMutations, mapGetters } from 'vuex'
import VConfirmDialog from '@/components/atoms/ConfirmDialog'

export default {
  components: {
    VConfirmDialog
  },

  props: {
    index: {
      type: Number,
      required: true,
    },
    isConfirm: {
      type: Boolean,
      default: false,
    }
  },

  data: () => ({
    rules: rules,
    duplicatedClassroomName: ''
  }),

  watch: {},

  computed: {
    ...mapGetters('enrollment',[
      'isAddClassroom'
    ]),
    classroom() {
      return this.$store.state.enrollment.classrooms[this.index - 1]
    },
    deleteMessage() {
      return `教室名「${this.classroom.name} 」を削除します。よろしいですか。`
    },
    // 全く同じ入力情報の教室が存在するか
    isDuplicatedClassroom() {
      // 入力内容に空の部分があれば重複チェックは行わない
      const requiredInputCount = this.isAddClassroom ? 5 : 4
      if (Object.values(this.classroom).filter(v => !!v).length !== requiredInputCount) return false

      let result = false
      this.$store.state.enrollment.classrooms.forEach((otherClassroom, index) => {
        if (index + 1 === this.index) return
        const diff = omitBy(this.classroom, (v, k) => otherClassroom[k] === v)
        if (Object.keys(diff).length === 0) {
          result = true
          this.duplicatedClassroomName = otherClassroom.name
        }
      })
      this.setHasDuplicatedError({ hasError: result})
      return result
    },
  },

  methods: {
    ...mapMutations('enrollment', [
      'setClassroomPayload',
      'setHasDuplicatedError',
      'removeClassroom'
    ]),
    updateClassroom(key, value) {
      var payload = {
        index: this.index - 1,
        classroom: {[key]: value}
      }
      this.setClassroomPayload(payload)
    },
    remove() {
      this.$refs.confirm.open()
    },
    confirmDeletion() {
      this.removeClassroom({ index: this.index - 1})
    },
  }
}
</script>

<style lang="stylus" scoped>

th
  width 30%
  height 30px
  background-color #3b7cab
  text-align left
  color #fff
  font-size 14px
  vertical-align top
  padding 9px
  border-bottom 1px solid #ddd

td
  width 1500px
  background-color #fff1f8
  border 1px solid white

.start-time
  &__th
    background-color: #fff1f8;
    max-width 200px
    border none
  &__td
    font-size 13px
    border none
    text-align left

.text-field
  width 220px
  margin 10px
  min-height 10px
  background-color white
  &__file
    width 400px
    margin 10px
    min-height 10px


.attention
  font-size 12px
  margin-bottom 5px

.hint
  font-size 11px
  color #252525
  margin-bottom 5px

.address
  &__area
    height 25px
  &__mark
    display inline-block
    font-size 14px

.input__address
  display inline-block
  border 1px solid #a8a8a8
  border-radius 4px
  background-color white
  width 100px
  padding 0px 4px

.tooltip__text
  font-weight bold

.question
  font-size 16px
  text-shadow 0px 1px white
  color #ff4a3d !important

.delete-icon
  margin-left 5px
  margin-bottom 5px

.duplicated__message
  display inline-block
  font-size 12px
  color red
  font-weight bold

</style>
